import { useEffect, useRef, useState } from 'react';
import {
  profileImageUrl,
  useMyWorker,
  useSaveProfile,
} from '@/hooks/useProfile';
import type { WorkerProfileEdits } from '@/hooks/useProfile';
import { pendingWorkerCommand } from '@/services/workerRegistration';
import { useSkillsAndRoles } from '@/hooks/useSkillsAndRoles';
import { WorkerSettings } from '@/components/features/profile/WorkerSettings';
import { AvailabilityPopover } from '@/components/features/availability/AvailabilityPopover';
import { validateImage } from '@/lib/auth-validation';
import { LANGUAGES } from '@/constants/languages';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { InfoItem } from '@/components/features/profile/InfoItem';
import type { WorkerProfile } from '@/types/session';
import { ReviewsList } from '@/components/features/ratings/ReviewsList';
import { StarRating } from '@/components/features/ratings/StarRating';
import { ToggleSwitch } from '@/components/ui/ToggleSwitch';
import { useProfileReviews } from '@/hooks/useProfileReviews';
import { reputationStars } from '@/lib/profile-reviews';
import { useDaoConfig } from '@/hooks/useDaoConfig';

export default function DeveloperProfilePage({
  profile: developer,
  principalId,
  startInEditMode,
}: {
  profile: WorkerProfile;
  principalId: string;
  startInEditMode?: boolean;
}) {
  const reviews = useProfileReviews('developer');
  const worker = useMyWorker();
  const catalog = useSkillsAndRoles();
  const { workerRole, externalProfile } = useDaoConfig();
  const mutation = useSaveProfile('worker');
  const [isEditing, setIsEditing] = useState(startInEditMode ?? false);
  const [availabilityOpen, setAvailabilityOpen] = useState(false);
  const [draft, setDraft] = useState(developer);
  const [image, setImage] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [revision, setRevision] = useState(0);
  const [error, setError] = useState('');
  const [status, setStatus] = useState('');
  const [workerEdits, setWorkerEdits] = useState<WorkerProfileEdits | null>(
    null
  );
  const fileInput = useRef<HTMLInputElement>(null);
  const avatarUrl = profileImageUrl(principalId, 'worker', revision);
  const languageNames = developer.languages.map(
    (code) => LANGUAGES[code] ?? code
  );
  const selectableDevRoleIds =
    worker.data?.qualifications.roleIds.filter((id) => id !== 1) ?? [];
  const getRoleName = (id: number) => catalog.getRoleName(id) ?? `Role #${id}`;
  const roleLevels = selectableDevRoleIds.map((id) => ({
    name: getRoleName(id),
    level:
      developer.roleExperiences?.find((entry) => entry.roleId === id)
        ?.proficiency ?? developer.proficiency,
  }));
  const experienceLabel =
    roleLevels.length === 0
      ? developer.proficiency || 'No proficiency level'
      : roleLevels.every((role) => role.level === roleLevels[0]?.level)
        ? roleLevels[0]?.level || 'No proficiency level'
        : roleLevels
            .map(
              (role) => `${role.name}: ${role.level || 'No proficiency level'}`
            )
            .join(' · ');
  const displayRole = workerRole;
  const rating = reputationStars(
    worker.data?.mode === 'Coordinator'
      ? worker.data.coordinatorScore
      : worker.data?.workerScore
  );
  const displaySkills =
    worker.data?.qualifications.skillIds.map(
      (id) =>
        catalog.skills.find((skill) => skill.id === id)?.name ?? `Skill #${id}`
    ) ?? [];
  useEffect(() => {
    if (!image) {
      setPreview(null);
      return;
    }
    const url = URL.createObjectURL(image);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [image]);
  useEffect(() => {
    if (!isEditing || workerEdits || !worker.data) return;
    try {
      const qualifications = pendingWorkerCommand(
        principalId,
        'qualifications'
      );
      const calendar = pendingWorkerCommand(principalId, 'calendar');
      setWorkerEdits({
        qualifications:
          qualifications?.payload.qualifications ?? worker.data.qualifications,
        hours: String(
          (calendar?.payload.defaultWeeklyMinutes ??
            worker.data.calendar.definition.defaultWeeklyMinutes) / 60
        ),
      });
      if (qualifications || calendar)
        setStatus(
          'Some changes await confirmation. Press Save to continue checking them.'
        );
    } catch (reason) {
      setError((reason as Error).message);
      setWorkerEdits({
        qualifications: worker.data.qualifications,
        hours: String(
          worker.data.calendar.definition.defaultWeeklyMinutes / 60
        ),
      });
    }
  }, [isEditing, workerEdits, worker.data, principalId]);
  const handleEnterEdit = () => {
    mutation.reset();
    setDraft(developer);
    setWorkerEdits(null);
    setImage(null);
    setError('');
    setStatus('');
    setIsEditing(true);
  };
  const save = async () => {
    setError('');
    try {
      await mutation.mutateAsync({
        profile: draft,
        image,
        worker: workerEdits ?? undefined,
        onStatus: setStatus,
      });
      setRevision(Date.now());
      setImage(null);
      setIsEditing(false);
    } catch (reason) {
      setStatus('');
      setError(
        reason instanceof Error ? reason.message : 'Profile save failed.'
      );
    }
  };
  if (isEditing)
    return (
      <div className="pageContent">
        <form
          className="formContainer min-w-0 text-left"
          onSubmit={(event) => {
            event.preventDefault();
            void save();
          }}
        >
          <div className="mb-4 text-left">
            <h1>Edit {workerRole} Profile</h1>
            <p>{workerRole} data</p>
          </div>
          <fieldset disabled={mutation.isPending} className="min-w-0 space-y-6">
            <div className="inputGroup">
              <p className="label">Email</p>
              <p className="readOnlyInput">
                {developer.contactEmail ?? 'No contact email'}
              </p>
            </div>
            {(
              [
                ['name', 'Name'],
                ['biography', 'Bio'],
                ['background', 'Background'],
                ['githubUsername', externalProfile.label],
                ['portfolioUrl', 'Portfolio URL'],
                ['location', 'Location'],
              ] as const
            ).map(([field, label]) => (
              <div key={field}>
                <div className="inputGroup">
                  <Label htmlFor={field}>{label}</Label>
                  {field === 'biography' || field === 'background' ? (
                    <textarea
                      id={field}
                      className="flex min-h-[100px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                      placeholder={
                        field === 'biography'
                          ? 'Short bio'
                          : 'Your professional background'
                      }
                      value={draft[field] ?? ''}
                      onChange={(event) =>
                        setDraft({
                          ...draft,
                          [field]: event.target.value || null,
                        })
                      }
                    />
                  ) : (
                    <Input
                      id={field}
                      required={field === 'name'}
                      placeholder={label}
                      value={draft[field] ?? ''}
                      onChange={(event) =>
                        setDraft({
                          ...draft,
                          [field]:
                            field === 'name'
                              ? event.target.value
                              : event.target.value || null,
                        })
                      }
                    />
                  )}
                </div>
                {field === 'background' && (!worker.data || workerEdits) && (
                  <WorkerSettings
                    kind="qualifications"
                    embedded
                    editor={
                      workerEdits
                        ? { value: workerEdits, onChange: setWorkerEdits }
                        : undefined
                    }
                    roleExperiences={draft.roleExperiences ?? []}
                    onExperienceChange={(roleId, proficiency) =>
                      setDraft((previous) => ({
                        ...previous,
                        roleExperiences: [
                          ...(previous.roleExperiences ?? []).filter(
                            (entry) => entry.roleId !== roleId
                          ),
                          { roleId, proficiency },
                        ],
                      }))
                    }
                  />
                )}
              </div>
            ))}
            <div className="inputGroup">
              <Label>Languages</Label>
              <div className="input flex max-h-48 flex-col space-y-1 overflow-y-auto rounded-md border border-input bg-background p-2">
                {Object.entries({
                  ...LANGUAGES,
                  ...Object.fromEntries(
                    draft.languages.map((code) => [
                      code,
                      LANGUAGES[code] ?? code,
                    ])
                  ),
                }).map(([code, name]) => (
                  <label
                    key={code}
                    className={`flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-sm transition-colors ${draft.languages.includes(code) ? 'bg-[--colors-emerald-50] !text-[--colors-emerald-500]' : ''}`}
                  >
                    <input
                      type="checkbox"
                      className="customCheckbox"
                      checked={draft.languages.includes(code)}
                      onChange={() =>
                        setDraft({
                          ...draft,
                          languages: draft.languages.includes(code)
                            ? draft.languages.filter((value) => value !== code)
                            : [...draft.languages, code],
                        })
                      }
                    />
                    <span>
                      {code} - {name}
                    </span>
                  </label>
                ))}
              </div>
            </div>
            {(!worker.data || workerEdits) && (
              <WorkerSettings
                kind="calendar"
                embedded
                editor={
                  workerEdits
                    ? { value: workerEdits, onChange: setWorkerEdits }
                    : undefined
                }
              />
            )}
            <div className="inputGroup">
              <Label>Profile Photo (PNG, JPEG or WebP, max 1 MiB)</Label>
              <div className="mt-2 flex items-center gap-4">
                <img
                  key={preview || avatarUrl}
                  src={preview || avatarUrl}
                  alt="Profile preview"
                  className="aspect-square w-[72px] overflow-hidden rounded-full border-2 border-border object-cover"
                  onError={(event) => {
                    event.currentTarget.onerror = null;
                    event.currentTarget.src = '/images/none.png';
                  }}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => fileInput.current?.click()}
                >
                  Select file...
                </Button>
                <input
                  ref={fileInput}
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  className="hidden"
                  onChange={(event) => {
                    const file = event.target.files?.[0];
                    if (!file) return;
                    try {
                      validateImage(file);
                      setImage(file);
                      setError('');
                    } catch (reason) {
                      setError((reason as Error).message);
                      event.target.value = '';
                    }
                  }}
                />
              </div>
            </div>
          </fieldset>
          {error && (
            <div className="rounded-md border border-red-500/30 bg-red-500/15 p-3">
              <p role="alert" className="text-sm text-red-400">
                {error}
              </p>
            </div>
          )}
          {status && <p role="status">{status}</p>}
          <div className="flex w-full items-center justify-between gap-4 border-t border-border pt-4 [&>button]:min-w-[128px]">
            <Button
              type="button"
              variant="outline"
              disabled={mutation.isPending}
              onClick={() => {
                mutation.reset();
                setIsEditing(false);
                setImage(null);
                setWorkerEdits(null);
                setError('');
                setStatus('');
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              isLoading={mutation.isPending}
              disabled={
                worker.isPending ||
                worker.isError ||
                (!!worker.data && !workerEdits)
              }
            >
              Save
            </Button>
          </div>
        </form>
      </div>
    );
  return (
    <div className="flex w-full flex-col">
      {/* Full-width header (Hero block, node 1350:15713) */}
      <div className="w-full border-b border-[--base-border] bg-[--base-surface-2]">
        <div className="px-4 py-8 sm:px-14">
          <div className="flex flex-wrap items-center justify-between gap-6">
            {/* Left: Avatar + Name + ID */}
            <div className="flex min-w-0 items-center gap-6">
              {/* Avatar 72px */}
              <div className="relative flex h-[72px] w-[72px] shrink-0 items-center justify-center overflow-hidden rounded-full border border-[--base-border] bg-[--base-surface-1]">
                <i className="ri-user-line text-3xl text-[--text-dark-tertiary]" />
                {avatarUrl && (
                  <img
                    key={avatarUrl}
                    src={avatarUrl}
                    alt={developer.name}
                    className="absolute inset-0 h-full w-full object-cover"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                )}
              </div>

              {/* Name + ID hash */}
              <div className="min-w-0 flex-1">
                <div className="flex items-baseline gap-4">
                  <span className="truncate text-3xl font-bold text-[--text-dark-primary]">
                    {developer.name}
                  </span>
                  <span className="shrink-0 text-xs font-medium text-[--text-dark-tertiary]">
                    {principalId.slice(0, 14)}…
                  </span>
                </div>
              </div>
            </div>

            {/* Right: DAO View button + toggle + rating */}
            <div className="flex shrink-0 items-center gap-8">
              {/* DAO View button */}
              <button
                type="button"
                disabled
                title="DAO profile integration pending"
                className="flex h-9 items-center gap-1.5 rounded-xl border border-[var(--state-brand-active,#36d399)] bg-[rgba(54,211,153,0.08)] px-3 text-sm font-semibold leading-[22px] text-[var(--state-brand-active,#36d399)] transition-colors hover:bg-[rgba(54,211,153,0.15)]"
              >
                <i
                  className="ri-government-line text-base leading-none"
                  aria-hidden="true"
                />
                DAO View
              </button>

              <div className="flex items-center gap-3">
                <button
                  type="button"
                  aria-label="Edit weekly availability"
                  aria-haspopup="dialog"
                  aria-expanded={availabilityOpen}
                  onClick={() => setAvailabilityOpen(true)}
                  disabled={worker.isPending || worker.isError}
                >
                  <ToggleSwitch
                    checked={
                      (worker.data?.calendar.definition.defaultWeeklyMinutes ??
                        0) > 0
                    }
                  />
                </button>
                <span className="whitespace-nowrap text-sm font-medium">
                  Available for Work
                </span>
              </div>
              <AvailabilityPopover
                open={availabilityOpen}
                onClose={() => setAvailabilityOpen(false)}
                placement="profile"
              />
              {!worker.isPending && !worker.isError && rating !== null && (
                <StarRating rating={rating} size="md" />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Content: two columns */}
      <div className="pageContent">
        <div className="flex w-full flex-col items-stretch gap-[--spacing-17] xl:flex-row xl:items-start">
          {/* Left column: Profile card */}
          <div className="min-w-0 flex-1 rounded-[--radi-6] border border-[--base-border] bg-[--base-surface-2] px-[--spacing-12] py-[--spacing-10] text-left shadow-elevation-xs">
            <div className="flex flex-col gap-[--spacing-10]">
              {/* Card header: role badges + edit button */}
              <div className="flex items-center justify-between">
                <div className="flex flex-col gap-1.5">
                  <div className="flex flex-wrap items-center gap-2">
                    {selectableDevRoleIds.length > 0 ? (
                      selectableDevRoleIds.map((id) => {
                        const name = getRoleName(id);
                        return name ? (
                          <span
                            key={id}
                            className="rounded-full border border-emerald-500/20 bg-emerald-500/10 px-2.5 py-0.5 text-xs font-semibold capitalize text-[--state-brand-active]"
                          >
                            {name}
                          </span>
                        ) : null;
                      })
                    ) : (
                      <span className="text-lg font-medium text-[--text-dark-primary]">
                        {displayRole}
                      </span>
                    )}
                  </div>
                  <span className="text-xs font-normal capitalize text-[--text-dark-secondary]">
                    {experienceLabel}
                  </span>
                </div>
                <Button onClick={handleEnterEdit} variant="tertiary" size="sm">
                  Edit Information
                </Button>
              </div>

              {/* Bio */}
              <div className="flex flex-col gap-[--spacing-3]">
                <span className="text-sm font-medium text-[--text-dark-tertiary]">
                  Bio
                </span>
                <p className="text-sm font-medium text-[--text-dark-primary]">
                  {developer.biography || 'No bio available.'}
                </p>
              </div>

              {/* Background */}
              <div className="flex flex-col gap-[--spacing-3]">
                <span className="text-sm font-medium text-[--text-dark-tertiary]">
                  Background
                </span>
                <p className="text-sm font-medium text-[--text-dark-primary]">
                  {developer.background || 'No background information.'}
                </p>
              </div>

              {/* Skills chips */}
              {displaySkills.length > 0 && (
                <div className="flex flex-wrap gap-[--spacing-5]">
                  {displaySkills.map((skill) => (
                    <span
                      key={skill}
                      className="flex h-[32px] items-center rounded-[--radi-4] border border-[--base-border] bg-[--base-surface-2] px-[--spacing-7] text-sm font-medium text-[--text-dark-primary] shadow-elevation-xs"
                    >
                      {skill}
                    </span>
                  ))}
                </div>
              )}

              {/* Info items */}
              <div className="flex flex-col gap-[--spacing-8]">
                <InfoItem
                  icon="ri-translate-2"
                  text={
                    languageNames.length > 0
                      ? languageNames.join(', ')
                      : 'No languages'
                  }
                />
                <InfoItem
                  icon="ri-map-pin-line"
                  text={developer.location || 'No location'}
                />
                <InfoItem
                  icon="ri-earth-line"
                  text={developer.portfolioUrl || 'No portfolio URL'}
                />
                <InfoItem
                  icon={externalProfile.icon || 'ri-github-line'}
                  text={
                    developer.githubUsername ||
                    `No ${externalProfile.label.replace(/\s*\(optional\)/i, '')}`
                  }
                />
                <InfoItem
                  icon="ri-mail-line"
                  text={developer.contactEmail ?? 'No contact email'}
                />
              </div>
            </div>
          </div>

          {/* Right column: Reviews */}
          <div className="min-w-0 flex-1">
            <ReviewsList
              ratings={reviews.data ?? []}
              totalCount={reviews.data?.length ?? 0}
              isLoading={reviews.isPending && !reviews.error}
            />
            {reviews.error && (
              <div role="alert">
                <p>{reviews.error.message}</p>
                <Button onClick={() => void reviews.refetch()}>
                  Retry reviews
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
